import React, { memo, useCallback } from "react";
import { View, Text, TextInput } from "react-native";
import LhDatePicker from "../../../../components/LhDatePicker";
import { EmployeeDetails } from "./types";
import { useTheme } from "../../../../theme";

interface TouchPointPulseCheckProps {
  employeeDetails: EmployeeDetails;
  handleEmployeeDetailChange: (
    field: keyof EmployeeDetails,
    value: any
  ) => void;
  styles: any;
  isViewOnlyMode: boolean;
}

// Create a simple functional component
const TouchPointPulseCheck = memo(
  ({
    employeeDetails,
    handleEmployeeDetailChange,
    styles,
    isViewOnlyMode,
  }: TouchPointPulseCheckProps) => {
    const { colors, resolvedTheme } = useTheme();
    const isDarkTheme = resolvedTheme === "dark";
    const readOnlyFieldBackground = isDarkTheme
      ? colors.background.elevated
      : "#D0D0D0";
    const fieldTextColor = isDarkTheme ? colors.text.primary : undefined;
    const fieldBorderColor = isDarkTheme ? colors.border.default : "#ced4da";

    // Use useCallback for the date change handler to prevent unnecessary re-renders
    const handleDateChange = useCallback(
      (date: Date | null) => {
        if (
          !employeeDetails.graduationDate ||
          !date ||
          employeeDetails.graduationDate.getTime() !== date.getTime()
        ) {
          handleEmployeeDetailChange("graduationDate", date);
        }
      },
      [employeeDetails.graduationDate, handleEmployeeDetailChange]
    );

    return (
      <View style={styles.inputGroupsRow}>
        <View style={styles.inputGroup}>
          <View style={styles.labelColumn}>
            <Text style={styles.formLabel} numberOfLines={1} ellipsizeMode="tail">New Hire Name</Text>
          </View>
          <View style={styles.inputColumn}>
            <TextInput
              style={[
                styles.formInput,
                {
                  backgroundColor: readOnlyFieldBackground,
                  borderColor: fieldBorderColor,
                  textAlign: "left",
                  height: 40,
                  color: fieldTextColor,
                },
              ]}
              placeholder="Employee Name"
              placeholderTextColor={
                isDarkTheme ? colors.text.secondary : undefined
              }
              value={employeeDetails.name}
              editable={false}
            />
          </View>
        </View>
        <View style={styles.inputGroup}>
          <View style={styles.labelColumn}>
            <Text style={styles.formLabel} numberOfLines={1} ellipsizeMode="tail">Peer Coach</Text>
          </View>
          <View style={styles.inputColumn}>
            <TextInput
              style={[
                styles.formInput,
                {
                  backgroundColor: readOnlyFieldBackground,
                  borderColor: fieldBorderColor,
                  textAlign: "left",
                  height: 40,
                  color: fieldTextColor,
                },
              ]}
              placeholder="Peer Coach Name"
              placeholderTextColor={
                isDarkTheme ? colors.text.secondary : undefined
              }
              value={employeeDetails.peerCoach || "Not assigned"}
              editable={false}
            />
          </View>
        </View>
        <View style={styles.inputGroup}>
          <View style={styles.labelColumn}>
            <Text style={styles.formLabel} numberOfLines={1} ellipsizeMode="tail">Expected Graduation Date</Text>
          </View>
          <View style={styles.inputColumn}>
            <View
              style={[
                styles.formInput,
                {
                  backgroundColor: readOnlyFieldBackground,
                  borderColor: fieldBorderColor,
                  height: 40,
                  justifyContent: "left",
                  paddingVertical: 0,
                } // match TextInput
              ]}
            >
              <LhDatePicker
                value={employeeDetails.graduationDate}
                onChange={handleDateChange}
                placeholder="Select graduation date"
                error={false}
                disabled={true}
                colorScheme={isDarkTheme ? "dark" : "light"}
                iconColor={isDarkTheme ? colors.text.secondary : "#555555"}
                style={{
                  containerStyle: {
                    height: 40,
                    backgroundColor: readOnlyFieldBackground,
                    borderColor: fieldBorderColor,
                  },
                }} // enforce height match with TextInput and background
              />
            </View>
          </View>
        </View>
        <View style={styles.inputGroup}>
          <View style={styles.labelColumn}>
            <Text style={styles.formLabel} numberOfLines={1} ellipsizeMode="tail">Weeks Until Graduation</Text>
          </View>
          <View style={styles.inputColumn}>
            <TextInput
              style={[
                styles.formInput,
                {
                  backgroundColor: readOnlyFieldBackground,
                  borderColor: fieldBorderColor,
                  textAlign: "left",
                  height: 40,
                  color: fieldTextColor,
                },
              ]}
              placeholder="N/A"
              placeholderTextColor={
                isDarkTheme ? colors.text.secondary : undefined
              }
              value={
                employeeDetails.weeksTillGraduation !== null
                  ? employeeDetails.weeksTillGraduation.toString()
                  : "N/A"
              }
              editable={false}
            />
          </View>
        </View>
      </View>
    );
  }
);

export default TouchPointPulseCheck;
